/**
 * ADO.NET connection strings for PostgreSQL (Npgsql) and MySQL (MySqlConnector) — Option C C3 ruling after review r5.
 * The pairs come from .NET's own connection-string parser (`scanDotNet(text, 'sqlclient')`: both builders derive from
 * `DbConnectionStringBuilder`, whose rules SqlClient shares); the caller has checked every key is the provider's.
 *
 *   Server=contoso.postgres.database.azure.com;Database=postgres;Port=5432;User Id=admin;Password=…;Ssl Mode=Require;
 *   Server=contoso.mysql.database.azure.com; Port=3306; Database=shop; Uid=admin; Pwd=…; SslMode=Required;
 *
 * Mapping: the host, port, database, user and password; SSL Mode → SSL (on unless Disable / None). Documented keywords
 * the form has no field for are listed as not used. Npgsql's Host may carry the port (`host:5433`, `[::1]:5433`): as
 * Npgsql does for a single host (C3 review r6, C3-24), it is split into Host and Port — the Host's port wins over
 * `Port=` — and a Host port that is not a number is a string Npgsql fails on.
 */
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { KeyValuePair } from '@/core/utils/connectionStringTokens';
import type { ParserMatch } from './types';
import { MYSQLCONNECTOR_SSL_MODES, NPGSQL_SSL_MODES } from './connectionStringKeywords';

type Provider = {
  engine: 'pgsql' | 'mysql';
  server: readonly string[];
  database: readonly string[];
  login: readonly string[];
  password: readonly string[];
  sslMode: readonly string[];
  /** The SSL Mode names the builder parses, in enum order (a number is the enum's value). */
  sslModes: readonly string[];
  /** The SSL Mode values that turn SSL off. */
  sslOff: readonly string[];
  /** Npgsql: a single Host may end with `:port`. */
  hostPort?: true;
};

const NPGSQL: Provider = {
  engine: 'pgsql',
  server: ['host', 'server'],
  database: ['database', 'db'],
  login: ['username', 'user name', 'userid', 'user id', 'uid'],
  password: ['password', 'psw', 'pwd'],
  sslMode: ['ssl mode', 'sslmode'],
  sslModes: NPGSQL_SSL_MODES,
  sslOff: ['disable'],
  hostPort: true,
};

const MYSQLCONNECTOR: Provider = {
  engine: 'mysql',
  server: ['server', 'host', 'data source', 'datasource', 'address', 'addr', 'network address'],
  database: ['database', 'initial catalog'],
  login: ['user id', 'userid', 'username', 'uid', 'user name', 'user'],
  password: ['password', 'pwd'],
  sslMode: ['ssl mode', 'sslmode'],
  // `Disabled` is `None`'s alias (both 0).
  sslModes: MYSQLCONNECTOR_SSL_MODES,
  sslOff: ['none', 'disabled'],
};

/**
 * Npgsql's `TrySplitHostPort` for a single host: the last `:` separates the port when the host has no other colon, or
 * when it closes a bracketed IPv6 address. Null: nothing to split (several hosts, a socket directory, a bare IPv6
 * address); `invalid`: a port Npgsql cannot parse.
 */
function npgsqlHostPort(raw: string): { host: string; port: string } | 'invalid' | null {
  const host = raw.trim();
  if (!host || host.includes(',') || host.startsWith('/') || host.startsWith('@') || /^[A-Za-z]:[\\/]/u.test(host)) return null;
  const separator = host.lastIndexOf(':');
  if (separator === -1) return null;
  const otherColon = host.lastIndexOf(':', separator - 1);
  const ipv6End = host.lastIndexOf(']');
  if (otherColon !== -1 && !(separator > ipv6End && otherColon < ipv6End)) return null;
  const port = host.slice(separator + 1).trim();
  if (!/^[+-]?\d+$/u.test(port)) return 'invalid';
  const name = host.slice(0, separator);
  return { host: name.startsWith('[') && name.endsWith(']') ? name.slice(1, -1) : name, port: String(Number(port)) };
}

/** The SSL Mode name a value parses as, or undefined when the builder would throw on it. */
function sslModeName(provider: Provider, raw: string): string | undefined {
  const value = raw.trim().toLowerCase();
  if (/^\d+$/u.test(value)) return provider.sslModes[Number(value)] ?? value;
  if (provider.engine === 'mysql' && value === 'disabled') return value;
  return provider.sslModes.includes(value) ? value : undefined;
}

function providerMatch(provider: Provider, pairs: readonly KeyValuePair[]): ParserMatch | null {
  const values: Partial<ConnectionFormValues> = {};
  const matchedFields: string[] = [];
  const unmatchedKeys: string[] = [];
  let recognized = false;
  const set = <K extends 'server' | 'port' | 'database' | 'login' | 'password'>(field: K, label: string, value: string) => {
    recognized = true;
    values[field] = value;
    if (!matchedFields.includes(label)) matchedFields.push(label);
  };

  for (const pair of pairs) {
    const key = pair.key.trim().toLowerCase();
    if (provider.server.includes(key)) set('server', 'server', pair.value);
    else if (key === 'port') set('port', 'port', pair.value);
    else if (provider.database.includes(key)) set('database', 'database', pair.value);
    else if (provider.login.includes(key)) set('login', 'user', pair.value);
    else if (provider.password.includes(key)) set('password', 'password', pair.value);
    else if (provider.sslMode.includes(key)) {
      const mode = sslModeName(provider, pair.value) ?? '';
      recognized = true;
      values.ssl = !provider.sslOff.includes(mode);
      if (values.ssl && !matchedFields.includes('SSL')) matchedFields.push('SSL');
    } else unmatchedKeys.push(pair.key);
  }
  if (provider.hostPort && typeof values.server === 'string') {
    const split = npgsqlHostPort(values.server);
    if (split && split !== 'invalid') {
      values.server = split.host;
      values.port = split.port;
      if (!matchedFields.includes('port')) matchedFields.push('port');
    }
  }
  return recognized ? { format: 'adonet', engine: provider.engine, values, matchedFields, unmatchedKeys } : null;
}

/** Whether the provider's builder parses every SSL Mode value in `pairs` (it throws on any other — not its string). */
function accepts(provider: Provider, pairs: readonly KeyValuePair[]): boolean {
  const sslModes = pairs.every((pair) => !provider.sslMode.includes(pair.key.trim().toLowerCase()) || sslModeName(provider, pair.value) !== undefined);
  // The effective Host (a later synonym overrides) with a port Npgsql cannot parse.
  const host = [...pairs].reverse().find((pair) => provider.server.includes(pair.key.trim().toLowerCase()));
  return sslModes && !(provider.hostPort && host && npgsqlHostPort(host.value) === 'invalid');
}

export const npgsqlAccepts = (pairs: readonly KeyValuePair[]): boolean => accepts(NPGSQL, pairs);
export const mysqlConnectorAccepts = (pairs: readonly KeyValuePair[]): boolean => accepts(MYSQLCONNECTOR, pairs);

/** Npgsql's reading of `pairs` (every key already one of its keywords), or null. */
export const npgsqlMatch = (pairs: readonly KeyValuePair[]): ParserMatch | null => providerMatch(NPGSQL, pairs);

/** MySqlConnector's reading of `pairs` (every key already one of its keywords), or null. */
export const mysqlConnectorMatch = (pairs: readonly KeyValuePair[]): ParserMatch | null => providerMatch(MYSQLCONNECTOR, pairs);
